import { useFormikContext } from 'formik';
import { useRef, useState, useEffect } from 'react';

import { ButtonColors } from 'types';
import { EventFormValues } from 'store/api/event';
import { Input, Button, Checkbox, TimePicker } from 'components';
import {
  parseShowHours,
  SHOW_HOURS_STEP_MINUTES,
} from 'utils/show-hours-parser';

import * as Styles from './styles';
import { SHOW_HOURS_TEXT_BOX } from './feature';
import {
  keepLabel,
  appendNotes,
  ScheduleFlags,
  showDateLabel,
  unclearMessage,
  copyFirstToAll,
  refusedMessage,
  ScheduleFormRow,
  applyParsedDays,
  scheduleRowError,
  MAX_SHOW_DAYS_APART,
} from './helpers';

interface ShowHoursProps {
  /** The form shows Notes (setup/teardown lines are added there). */
  notesVisible: boolean;
  /** Start and end are both set but too far apart to list. */
  datesTooLong?: boolean;
}

// How long typing pauses before the rows follow the text.
export const SHOW_HOURS_DEBOUNCE_MS = 300;

const BOX_NAME = 'showHoursText';

// Takes out the lines this box added (the last copy of each), never an
// identical line that was already in Notes before.
const withoutOwnLines = (notes: string, own: string[]) => {
  const lines = notes.split('\n');
  own.forEach((line) => {
    const at = lines.lastIndexOf(line);
    if (at >= 0) {
      lines.splice(at, 1);
    }
  });
  return lines.join('\n');
};

/**
 * Show hours (owner, 23 Oct): a text box the team types or pastes the
 * hours into, and one row per show date that follows it live.
 * - Rows the box fills are rebuilt from the text every time (from how they
 *   were before the box was first used), so deleting a line undoes it and
 *   clearing the box restores them.
 * - A row changed by hand is "Edited": the box skips it until "Use text".
 * - Setup / teardown lines go to Notes when the box is left (or the form is
 *   saved with the box focused), replacing the lines it added before.
 */
export const ShowHours = ({ notesVisible, datesTooLong }: ShowHoursProps) => {
  const {
    errors,
    values,
    submitForm,
    submitCount,
    isSubmitting,
    setFieldValue,
  } = useFormikContext<EventFormValues>();
  const rows = (values.schedule ?? []) as ScheduleFormRow[];
  const datesKey = rows.map(({ date }) => date).join(',');

  // The section (or, with no rows, its hint): finds the form to guard.
  const sectionRef = useRef<HTMLDivElement & HTMLParagraphElement>(null);
  const [text, setText] = useState('');
  const [flags, setFlags] = useState<ScheduleFlags>({});
  const flagsRef = useRef(flags);
  flagsRef.current = flags;
  const [unread, setUnread] = useState<string[]>([]);
  const [edited, setEdited] = useState<string[]>([]);
  const [addedNotes, setAddedNotes] = useState<string[]>([]);
  // A From / To box left with a refused time ("10:52"), by "date.from".
  // Save waits while any is open (review, 23 Oct: the box kept its old
  // time and Save stored it): the dates of the CURRENT rows with an open
  // note go to a form-only field the form's validation checks, kept in
  // step with the rows like the yellow-row list (a date the show no longer
  // has never blocks).
  const [typedProblems, setTypedProblemsState] = useState<
    Record<string, string>
  >({});
  const typedProblemsRef = useRef(typedProblems);
  const refusedDatesOf = (problems: Record<string, string>) =>
    rows
      .map(({ date }) => date)
      .filter((date) => problems[`${date}.from`] || problems[`${date}.to`]);
  const storedRefused = (values.scheduleRefused ?? []).join(',');
  const setTypedProblems = (next: Record<string, string>) => {
    typedProblemsRef.current = next;
    setTypedProblemsState(next);
    // At once (a Save right after leaving the box sees it); the effect
    // below keeps it in step with the rows after that.
    const dates = refusedDatesOf(next);
    if (dates.join(',') !== storedRefused) {
      setFieldValue('scheduleRefused', dates);
    }
  };
  /** Sets (a reason) or clears (none) the refused-time note of boxes. */
  const setTypedProblem = (keys: string | string[], problem?: string) => {
    const current = typedProblemsRef.current;
    const next = { ...current };
    (Array.isArray(keys) ? keys : [keys]).forEach((key) => {
      if (problem) {
        next[key] = problem;
      } else {
        delete next[key];
      }
    });
    const changed = Object.keys({ ...current, ...next }).some(
      (key) => next[key] !== current[key]
    );
    if (changed) {
      setTypedProblems(next);
    }
  };

  // Rows as they were before the box was first used (by date).
  const baseRef = useRef<Record<string, ScheduleFormRow> | null>(null);
  // Times of a row while it is Closed, back when Closed is unticked.
  const closedTimesRef = useRef<
    Record<string, Pick<ScheduleFormRow, 'to' | 'from'>>
  >({});

  // Every change made here marks the hours as edited: only then does the
  // form send them (and only when they differ from what was loaded).
  const setRows = (next: ScheduleFormRow[]) => {
    setFieldValue('schedule', next);
    setFieldValue('scheduleTouched', true);
  };

  // Flags and the form's Save block move together, at once: a Save right
  // after the text changed sees the same yellow rows the page shows.
  const updateFlags = (next: ScheduleFlags) => {
    setFlags(next);
    const dates = rows
      .map(({ date }) => date)
      .filter((date) => next[date]?.kind === 'unclear');
    if ((values.scheduleUnclear ?? []).join(',') !== dates.join(',')) {
      setFieldValue('scheduleUnclear', dates);
    }
    return dates;
  };

  /** Reads the text into the rows; returns the dates left yellow. */
  const applyText = (boxText: string, editedDates: string[]): string[] => {
    if (!rows.length) {
      return [];
    }
    // Rows the text (or clearing it) sets again: their old "refused time"
    // notes are stale. With no text and nothing to restore (the dates
    // changed), no row is set and every note stays.
    if (boxText.trim() || baseRef.current) {
      const current = typedProblemsRef.current;
      const kept = Object.fromEntries(
        Object.entries(current).filter(([key]) =>
          editedDates.includes(key.split('.')[0])
        )
      );
      if (Object.keys(kept).length !== Object.keys(current).length) {
        setTypedProblems(kept);
      }
    }
    if (!boxText.trim()) {
      const base = baseRef.current;
      if (base) {
        setRows(
          rows.map((row) =>
            editedDates.includes(row.date) ? row : (base[row.date] ?? row)
          )
        );
      }
      baseRef.current = null;
      setUnread([]);
      return updateFlags({});
    }
    if (!baseRef.current) {
      baseRef.current = Object.fromEntries(
        rows.map((row) => [row.date, row] as const)
      );
    }
    const base = baseRef.current;
    const start = rows.map((row) =>
      editedDates.includes(row.date) ? row : (base[row.date] ?? row)
    );
    const result = parseShowHours(
      boxText,
      rows.map(({ date }) => date)
    );
    const applied = applyParsedDays(start, result.days);
    setRows(
      applied.rows.map((row, i) =>
        editedDates.includes(row.date) ? rows[i] : row
      )
    );
    const nextFlags = { ...applied.flags };
    // A hand-edited row keeps its own flag (still yellow until settled by
    // hand) and never takes a new one from the text.
    editedDates.forEach((date) => {
      delete nextFlags[date];
      if (flagsRef.current[date]) {
        nextFlags[date] = flagsRef.current[date];
      }
    });
    setUnread(result.unread);
    return updateFlags(nextFlags);
  };

  // Live: the rows follow the text a moment after typing stops (and when
  // the show's dates change).
  const applyRef = useRef(applyText);
  applyRef.current = applyText;
  const editedRef = useRef(edited);
  editedRef.current = edited;
  const textRef = useRef(text);
  textRef.current = text;
  const rowsRef = useRef(rows.length);
  rowsRef.current = rows.length;
  // The waiting read, if any: Save runs it at once (no 300 ms gap).
  const pendingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current || !SHOW_HOURS_TEXT_BOX) {
      firstRun.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      pendingRef.current = null;
      applyRef.current(text, editedRef.current);
    }, SHOW_HOURS_DEBOUNCE_MS);
    pendingRef.current = timer;
    return () => {
      clearTimeout(timer);
      pendingRef.current = null;
    };
  }, [text, datesKey]);

  const notesLines = () =>
    text.trim() && rows.length
      ? parseShowHours(
          text,
          rows.map(({ date }) => date)
        ).notes
      : [];

  const commitNotes = () => {
    if (!notesVisible) {
      return;
    }
    const lines = notesLines();
    const current = values.notes ?? '';
    const kept = withoutOwnLines(current, addedNotes);
    // Only lines Notes doesn't already hold are added, and only those are
    // this box's to take out later.
    const fresh = lines.filter((line) => !kept.split('\n').includes(line));
    const next = appendNotes(kept, fresh);
    if (next !== current) {
      setFieldValue('notes', next);
    }
    setAddedNotes(fresh);
  };

  // Saving with the box still focused: its Notes lines go in first.
  const commitRef = useRef(commitNotes);
  commitRef.current = commitNotes;
  const submitRef = useRef(submitForm);
  submitRef.current = submitForm;
  // A Save stopped here because the text was still being read.
  const [heldSave, setHeldSave] = useState(false);

  // One save at a time (6th pass: a double click made two events). A
  // submit is in flight from the moment one is let through (or held while
  // the text is read) until Formik's isSubmitting ends, which the event form
  // keeps true until its request has answered, success or failure.
  const heldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const passedRef = useRef(false);
  const releaseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sawSubmittingRef = useRef(false);
  const submittingRef = useRef(isSubmitting);
  submittingRef.current = isSubmitting;
  useEffect(() => {
    if (isSubmitting) {
      sawSubmittingRef.current = true;
    } else if (sawSubmittingRef.current) {
      sawSubmittingRef.current = false;
      passedRef.current = false;
    }
  }, [isSubmitting]);
  const markPassed = () => {
    passedRef.current = true;
    sawSubmittingRef.current = false;
    // Safety: if the form never starts submitting, free the guard.
    if (releaseRef.current) {
      clearTimeout(releaseRef.current);
    }
    releaseRef.current = setTimeout(() => {
      if (!sawSubmittingRef.current && !submittingRef.current) {
        passedRef.current = false;
      }
    }, 1000);
  };

  useEffect(() => {
    const form = sectionRef.current?.closest('form');
    if (!form) {
      return undefined;
    }
    const onSubmit = (event: Event) => {
      if (heldTimerRef.current || passedRef.current || submittingRef.current) {
        // A save is already on its way: this click or Enter does nothing.
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (SHOW_HOURS_TEXT_BOX) {
        const box = sectionRef.current?.querySelector(
          `textarea[id="${BOX_NAME}"]`
        );
        if (box && document.activeElement === box) {
          commitRef.current();
        }
      }
      if (!SHOW_HOURS_TEXT_BOX || !pendingRef.current || !rowsRef.current) {
        // Nothing to read first (or no rows to fill): save now.
        markPassed();
        return;
      }
      // The text changed less than 300 ms ago: read it now, and hold this
      // Save so the form checks what is typed, not the rows from before.
      clearTimeout(pendingRef.current);
      pendingRef.current = null;
      const yellow = applyRef.current(textRef.current, editedRef.current);
      event.preventDefault();
      event.stopPropagation();
      if (yellow.length) {
        setHeldSave(true);
        sectionRef.current
          ?.querySelector(`[data-testid="show-hours-row-${yellow[0]}"]`)
          ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      } else {
        // Nothing yellow: save as soon as the page shows the new rows.
        heldTimerRef.current = setTimeout(() => {
          heldTimerRef.current = null;
          markPassed();
          submitRef.current();
        }, 50);
      }
    };
    form.addEventListener('submit', onSubmit, true);
    return () => form.removeEventListener('submit', onSubmit, true);
  }, [datesKey]);

  // Save block: the dates still flagged unclear go to a form-only field the
  // form's validation checks ("Closed?" rows never block).
  const unclearDates = rows
    .map(({ date }) => date)
    .filter((date) => flags[date]?.kind === 'unclear');
  const unclearKey = unclearDates.join(',');
  useEffect(() => {
    const current = (values.scheduleUnclear ?? []).join(',');
    if (current !== unclearKey) {
      setFieldValue('scheduleUnclear', unclearKey ? unclearKey.split(',') : []);
    }
  }, [unclearKey]);
  // Dates with a refused From / To time (their red note is open), among
  // the current rows; the form's field always holds exactly these.
  const refusedDates = refusedDatesOf(typedProblems);
  const refusedKey = refusedDates.join(',');
  useEffect(() => {
    if (storedRefused !== refusedKey) {
      setFieldValue('scheduleRefused', refusedKey ? refusedKey.split(',') : []);
    }
  }, [refusedKey, storedRefused]);
  // A date the show no longer has drops its notes (they never come back).
  useEffect(() => {
    const keep = new Set(rows.map(({ date }) => date));
    const current = typedProblemsRef.current;
    const kept = Object.fromEntries(
      Object.entries(current).filter(([key]) => keep.has(key.split('.')[0]))
    );
    if (Object.keys(kept).length !== Object.keys(current).length) {
      setTypedProblems(kept);
    }
  }, [datesKey]);
  // Another event in the same form: nothing carries over.
  const eventId = (values as { id?: number }).id;
  const firstEvent = useRef(true);
  useEffect(() => {
    if (firstEvent.current) {
      firstEvent.current = false;
      return;
    }
    if (Object.keys(typedProblemsRef.current).length) {
      setTypedProblems({});
    }
  }, [eventId]);
  // A blocked Save scrolls to the first yellow row; else to the first box
  // with a refused time, and puts the cursor in it.
  useEffect(() => {
    if (submitCount > 0 && unclearDates.length) {
      sectionRef.current
        ?.querySelector(`[data-testid="show-hours-row-${unclearDates[0]}"]`)
        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    } else if (submitCount > 0 && refusedDates.length) {
      const date = refusedDates[0];
      const index = rows.findIndex((row) => row.date === date);
      const key = typedProblemsRef.current[`${date}.from`] ? 'from' : 'to';
      sectionRef.current
        ?.querySelector(`[data-testid="show-hours-row-${date}"]`)
        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      document
        .getElementById(`schedule.${index}.${key}`)
        ?.focus({ preventScroll: true });
    }
  }, [submitCount]);

  const clearFlag = (date: string) => {
    if (flags[date]) {
      const rest = { ...flags };
      delete rest[date];
      updateFlags(rest);
    }
  };

  const markEdited = (dates: string[]) => {
    const next = Array.from(new Set([...edited, ...dates]));
    setEdited(next);
    return next;
  };

  const updateRow = (index: number, patch: Partial<ScheduleFormRow>) => {
    const row = rows[index];
    const next = { ...row, ...patch };
    // Closed, or a range filling both boxes: no refused-time note is left.
    if (
      patch.dayOff !== undefined ||
      (patch.from !== undefined && patch.to !== undefined)
    ) {
      setTypedProblem([`${row.date}.from`, `${row.date}.to`]);
    }
    if (patch.dayOff === true) {
      // Closed: no times on the row (they come back if it is unticked).
      closedTimesRef.current[row.date] = { to: row.to, from: row.from };
      delete next.from;
      delete next.to;
    } else if (patch.dayOff === false) {
      Object.assign(next, closedTimesRef.current[row.date] ?? {});
      delete closedTimesRef.current[row.date];
    }
    (['from', 'to'] as const).forEach((key) => {
      if (next[key] === undefined) {
        delete next[key];
      }
    });
    setRows(rows.map((item, i) => (i === index ? next : item)));
    markEdited([row.date]);
    // A yellow row is settled by hand once it has both times or is Closed.
    if (next.dayOff || (next.from !== undefined && next.to !== undefined)) {
      clearFlag(row.date);
    }
  };

  // "Keep 10:00 am – 9:00 pm": the row stays as it is, its flag goes, and
  // the box no longer changes it (no link when the row has no hours).
  const keepRow = (date: string) => {
    clearFlag(date);
    markEdited([date]);
  };

  const returnToText = (date: string) => {
    const next = edited.filter((item) => item !== date);
    setEdited(next);
    applyText(text, next);
  };

  if (!rows.length) {
    return (
      <Styles.Hint ref={sectionRef} data-testid="show-hours-empty">
        {datesTooLong
          ? `These dates are more than ${MAX_SHOW_DAYS_APART} days apart. Check the dates to enter show hours.`
          : "Pick the show's dates to enter its hours."}
      </Styles.Hint>
    );
  }

  const boxInUse = Boolean(text.trim());
  const pendingNotes = boxInUse ? notesLines() : [];

  return (
    <Styles.Section ref={sectionRef} data-testid="show-hours">
      {SHOW_HOURS_TEXT_BOX && (
        <Styles.PastePanel>
          <Input
            value={text}
            type="textarea"
            name={BOX_NAME}
            onBlur={commitNotes}
            label="Type or paste the show's hours"
            placeholder="Fri–Sun 10am–6pm · Mon closed"
            onChange={(event) => setText(event.target.value)}
          />
          <Styles.BoxHelp data-testid="show-hours-help">
            One line per day or range, e.g. Sat 10am–6pm
          </Styles.BoxHelp>
          {(unread.length > 0 || pendingNotes.length > 0) && (
            <Styles.Summary
              role="status"
              $warning={unread.length > 0}
              data-testid="show-hours-summary"
            >
              <Styles.SummaryText>
                {unread.length > 0 && (
                  <span data-testid="show-hours-unread">
                    Not sure about: {unread.join(' · ')}
                  </span>
                )}
                {pendingNotes.length > 0 && (
                  <span data-testid="show-hours-notes">
                    {notesVisible
                      ? 'Goes to Notes: '
                      : 'Not show hours (setup / teardown): '}
                    {pendingNotes.join(' · ')}
                  </span>
                )}
              </Styles.SummaryText>
            </Styles.Summary>
          )}
        </Styles.PastePanel>
      )}

      <Styles.Toolbar>
        <Button
          text="Copy first day to all"
          colorType={ButtonColors.LightBlue}
          onClick={() => {
            setRows(copyFirstToAll(rows));
            markEdited(rows.slice(1).map(({ date }) => date));
            // The other days take day 1's times: their red notes go.
            setTypedProblem(
              rows
                .slice(1)
                .flatMap(({ date }) => [`${date}.from`, `${date}.to`])
            );
            // Day 1 keeps its own flag (its hours are still unchecked).
            const first = rows[0] && flags[rows[0].date];
            updateFlags(first ? { [rows[0].date]: first } : {});
          }}
        />
      </Styles.Toolbar>

      {/* A refused time's block follows the open red notes themselves
          (never a validation result from before the time was fixed). */}
      {((submitCount > 0 && errors.scheduleUnclear) ||
        (submitCount > 0 && refusedDates.length > 0) ||
        (heldSave && unclearDates.length > 0)) && (
        <Styles.BlockMessage role="alert" data-testid="show-hours-block">
          {unclearDates.length
            ? unclearMessage(unclearDates)
            : refusedDates.length
              ? refusedMessage(refusedDates)
              : String(errors.scheduleUnclear)}
        </Styles.BlockMessage>
      )}

      <Styles.Rows>
        {rows.map((row, index) => {
          const flag = flags[row.date];
          const error = scheduleRowError(row);
          const showError =
            error &&
            (submitCount > 0 ||
              (row.from !== undefined && row.to !== undefined));
          // A refused typed time comes first: the box kept its old value.
          const typedProblem =
            typedProblems[`${row.date}.from`] ??
            typedProblems[`${row.date}.to`];
          const note = typedProblem ?? (showError ? error : flag?.reason);
          const isError = Boolean(typedProblem || showError);
          const isEdited = boxInUse && edited.includes(row.date);
          return (
            <Styles.Row
              key={row.date}
              $flagged={Boolean(flag)}
              data-testid={`show-hours-row-${row.date}`}
            >
              <Styles.DateLabel>{showDateLabel(row.date)}</Styles.DateLabel>
              <Styles.FromCell>
                <TimePicker
                  typeable
                  label="From"
                  timeRole="from"
                  placeholder="Opens"
                  isDisabled={row.dayOff}
                  step={SHOW_HOURS_STEP_MINUTES}
                  name={`schedule.${index}.from`}
                  value={row.dayOff ? undefined : row.from}
                  onChange={(from) => updateRow(index, { from })}
                  onRangeInput={(range) => updateRow(index, range)}
                  onTypedProblem={(problem) =>
                    setTypedProblem(`${row.date}.from`, problem)
                  }
                />
              </Styles.FromCell>
              <Styles.ToCell>
                <TimePicker
                  typeable
                  label="To"
                  timeRole="to"
                  after={row.from}
                  menuAlign="right"
                  placeholder="Closes"
                  isDisabled={row.dayOff}
                  name={`schedule.${index}.to`}
                  step={SHOW_HOURS_STEP_MINUTES}
                  value={row.dayOff ? undefined : row.to}
                  onChange={(to) => updateRow(index, { to })}
                  onRangeInput={(range) => updateRow(index, range)}
                  onTypedProblem={(problem) =>
                    setTypedProblem(`${row.date}.to`, problem)
                  }
                />
              </Styles.ToCell>
              <Styles.ClosedCell>
                <Checkbox
                  label="Closed"
                  checked={row.dayOff}
                  id={`schedule-${row.date}-closed`}
                  onChange={(event) =>
                    updateRow(index, { dayOff: event.target.checked })
                  }
                />
              </Styles.ClosedCell>
              {(note || isEdited) && (
                <Styles.RowNote
                  $error={isError}
                  data-testid={`show-hours-note-${row.date}`}
                >
                  {note}
                  {!isError && flag?.kind === 'unclear' && keepLabel(row) && (
                    <Styles.TextAction
                      type="button"
                      onClick={() => keepRow(row.date)}
                      data-testid={`show-hours-keep-${row.date}`}
                    >
                      {keepLabel(row)}
                    </Styles.TextAction>
                  )}
                  {isEdited && (
                    <Styles.EditedTag
                      data-testid={`show-hours-edited-${row.date}`}
                    >
                      Edited ·{' '}
                      <Styles.TextAction
                        type="button"
                        onClick={() => returnToText(row.date)}
                      >
                        Use text
                      </Styles.TextAction>
                    </Styles.EditedTag>
                  )}
                </Styles.RowNote>
              )}
            </Styles.Row>
          );
        })}
      </Styles.Rows>
    </Styles.Section>
  );
};
